import { useState } from 'react';
import { AiFillWarning } from 'react-icons/ai';
import { BiChevronDown, BiChevronUp, BiSubdirectoryRight } from 'react-icons/bi';
import { Link } from 'react-router-dom';
import General from '../../utils/GeneralUtils';
import UniversalTag from '../../custom-components/UniversalTag';
import './InstitutionEvidence.scss';

/** URL info with optional screenshot, type, and status */
interface UrlWithScreenshot {
  url: string;
  screenshotKey: string | null;
  urlType: string | null;
  urlStatus: string | null;
}

/** URL type priority for sorting (lower = higher priority) */
const URL_TYPE_PRIORITY: Record<string, number> = {
  'STAFF_DIRECTORY': 0,
  'STAFF_LEADERSHIP': 1,
  'STAFF_SUPERINTENDENT': 2,
  'STAFF_PROFILE': 3,
};

/** Evidence data per resolution method */
interface MethodEvidence {
  values: Set<string>;
  urls: Map<string, UrlWithScreenshot>;
}

/** Source type categories */
type SourceCategory = 'web' | 'district';

/** Evidence grouped by source type, then resolution method */
interface SourceTypeEvidence {
  [resolutionMethod: string]: MethodEvidence;
}

/** Evidence map per institution with recency tracking */
export interface InstitutionEvidenceMap {
  web: SourceTypeEvidence;
  district: SourceTypeEvidence;
  webMostRecentAt: string | null;      // Most recent observedAt for web evidence
  districtMostRecentAt: string | null; // Most recent observedAt for district evidence
}

/** Order resolution methods by strength */
const RESOLUTION_METHOD_ORDER = ['EMAIL_DOMAIN', 'URL_PREFIX', 'URL_DOMAIN', 'FREEFORM_NAME', 'FOIA'];

/** Aggregates evidence by source type (web/district) then resolution method per institution */
export function getInstitutionEvidence(row: any): Map<string, InstitutionEvidenceMap> {
  const evidenceMap = new Map<string, InstitutionEvidenceMap>();

  if (!row?.roles) return evidenceMap;

  for (const role of row.roles) {
    if (!role?.evidence) continue;
    for (const ev of role.evidence) {
      if (!ev?.observations) continue;
      const sourceUrl = ev?.url?.url;
      const screenshotKey = ev?.url?.screenshotKey || null;
      const urlType = ev?.url?.urlType || null;
      const urlStatus = ev?.url?.urlStatus || null;
      const sourceType = ev?.sourceType;
      const observedAt = ev?.observedAt || null;

      // Determine source category
      const sourceCategory: SourceCategory = (sourceType === 'STATE_DATA' || sourceType === 'FOIA')
        ? 'district'
        : 'web';

      for (const obs of ev.observations) {
        if (!obs?.institutionId || !obs?.resolutionMethod) continue;

        if (!evidenceMap.has(obs.institutionId)) {
          evidenceMap.set(obs.institutionId, {
            web: {},
            district: {},
            webMostRecentAt: null,
            districtMostRecentAt: null
          });
        }

        const instEvidence = evidenceMap.get(obs.institutionId)!;
        const categoryEvidence = instEvidence[sourceCategory];

        // Track most recent observation date per category
        if (observedAt) {
          const recencyKey = sourceCategory === 'web' ? 'webMostRecentAt' : 'districtMostRecentAt';
          if (!instEvidence[recencyKey] || observedAt > instEvidence[recencyKey]!) {
            instEvidence[recencyKey] = observedAt;
          }
        }

        if (!categoryEvidence[obs.resolutionMethod]) {
          categoryEvidence[obs.resolutionMethod] = { values: new Set(), urls: new Map() };
        }

        if (obs.resolutionValue) {
          categoryEvidence[obs.resolutionMethod].values.add(obs.resolutionValue);
        }
        if (sourceUrl) {
          const existing = categoryEvidence[obs.resolutionMethod].urls.get(sourceUrl);
          if (!existing || (screenshotKey && !existing.screenshotKey)) {
            categoryEvidence[obs.resolutionMethod].urls.set(sourceUrl, { url: sourceUrl, screenshotKey, urlType, urlStatus });
          }
        }
      }
    }
  }

  return evidenceMap;
}

/** Get institution type label */
function getInstitutionTypeLabel(institutionType: string | undefined): string {
  switch (institutionType) {
    case 'DISTRICT': return 'district';
    case 'SCHOOL': return 'school';
    case 'AGENCY': return 'agency';
    default: return 'institution';
  }
}

/** Extract all unique domains from source evidence */
function getAllDomains(sourceEvidence: SourceTypeEvidence): string[] {
  const domains = new Set<string>();
  for (const method of RESOLUTION_METHOD_ORDER) {
    const methodEvidence = sourceEvidence[method];
    if (methodEvidence?.urls?.size > 0) {
      for (const urlInfo of methodEvidence.urls.values()) {
        if (urlInfo.url) {
          try {
            const domain = new URL(urlInfo.url).hostname.replace(/^www\./, '');
            domains.add(domain);
          } catch {
            // skip invalid URLs
          }
        }
      }
    }
  }
  return Array.from(domains);
}

/** Format domains for display with "and" before last item, returns JSX with monospace styled domains */
function formatDomainsJsx(domains: string[]): React.ReactNode {
  if (domains.length === 0) return null;

  const DomainSpan = ({ domain }: { domain: string }) => (
    <span className="institution-evidence__domain">{domain}</span>
  );

  if (domains.length === 1) {
    return <DomainSpan domain={domains[0]} />;
  }
  if (domains.length === 2) {
    return <><DomainSpan domain={domains[0]} /> and <DomainSpan domain={domains[1]} /></>;
  }
  if (domains.length === 3) {
    return <><DomainSpan domain={domains[0]} />, <DomainSpan domain={domains[1]} />, and <DomainSpan domain={domains[2]} /></>;
  }
  return <><DomainSpan domain={domains[0]} />, <DomainSpan domain={domains[1]} />, and {domains.length - 2} more</>;
}

/** Format recency label based on observation date */
function formatRecency(dateStr: string | null): string {
  if (!dateStr) return '';

  const observedDate = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - observedDate.getTime();
  const diffMonths = diffMs / (1000 * 60 * 60 * 24 * 30);

  if (diffMonths < 3) return 'less than 3 months ago';
  if (diffMonths < 6) return '3-6 months ago';
  if (diffMonths < 12) return '6-12 months ago';
  return 'more than 12 months ago';
}

/** Generate natural language evidence description */
function getEvidenceDescription(method: string, values: string[], institutionType: string | undefined, firstName?: string): React.ReactNode {
  const typeLabel = getInstitutionTypeLabel(institutionType);
  const valueStr = values.join(', ');

  switch (method) {
    case 'EMAIL_DOMAIN':
      const namePrefix = firstName ? `${firstName}'s` : "Contact's";
      return <>{namePrefix} email domain <span className="institution-evidence__domain">@{valueStr}</span> belongs to this {typeLabel}</>;
    case 'URL_DOMAIN':
      const urlNamePrefix = firstName ? firstName : "Contact";
      return <>{urlNamePrefix} found on this {typeLabel}'s website at <span className="institution-evidence__domain">{valueStr}</span></>;
    case 'URL_PREFIX':
      const prefixNamePrefix = firstName ? firstName : "Contact";
      return `${prefixNamePrefix} found on this ${typeLabel}'s website`;
    case 'FREEFORM_NAME':
      return <>Page mentions {typeLabel} name <span className="institution-evidence__domain">{valueStr}</span></>;
    case 'FOIA':
      return `Listed in public records for this ${typeLabel}`;
    default:
      return `${method}: ${valueStr}`;
  }
}

/** Order institution types: DISTRICT > AGENCY > SCHOOL */
const INSTITUTION_TYPE_ORDER: Record<string, number> = {
  'DISTRICT': 0,
  'AGENCY': 1,
  'SCHOOL': 2,
};

/** Sort institutions by type (district first, then agency, then school) */
export function sortInstitutionsByType(belongsTo: any[]): any[] {
  return [...belongsTo].sort((a, b) => {
    const typeA = INSTITUTION_TYPE_ORDER[a?.institution?.type] ?? 99;
    const typeB = INSTITUTION_TYPE_ORDER[b?.institution?.type] ?? 99;
    return typeA - typeB;
  });
}

interface ScreenshotData {
  screenshot: string;
  screenshot_size: null;
  boxes: [];
}

interface CollapsibleEvidenceProps {
  emoji: string;
  label: React.ReactNode;
  recency: string;
  children: React.ReactNode;
}

function CollapsibleEvidence({ emoji, label, recency, children }: CollapsibleEvidenceProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="institution-evidence__category">
      <div className="institution-evidence__category-item">
        <div
          className="institution-evidence__category-header"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
        >
          <BiSubdirectoryRight className="institution-evidence__icon" size={18} />
          <span className="institution-evidence__label">
            {emoji} {label}{recency ? ` ${recency}` : ''}
          </span>
          {isExpanded ? (
            <BiChevronUp className="institution-evidence__chevron" size={16} />
          ) : (
            <BiChevronDown className="institution-evidence__chevron" size={16} />
          )}
        </div>
        {isExpanded && (
          <div className="institution-evidence__methods">
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

interface InstitutionEvidenceProps {
  belongsTo: any[];
  firstName?: string;
  institutionEvidence: Map<string, InstitutionEvidenceMap>;
  onSelectScreenshot: (data: ScreenshotData) => void;
}

export function InstitutionEvidence({
  belongsTo,
  firstName,
  institutionEvidence,
  onSelectScreenshot
}: InstitutionEvidenceProps) {
  const sortedInstitutions = sortInstitutionsByType(belongsTo);

  // Check if any school has evidence (for inferring district relationship)
  const hasSchoolWithEvidence = sortedInstitutions.some((bt: any) => {
    const ev = institutionEvidence.get(bt.institution?.id);
    return bt.institution?.type === 'SCHOOL' && ev &&
      (Object.keys(ev.web).length > 0 || Object.keys(ev.district).length > 0);
  });

  // Collect all URLs across all methods for web evidence
  const getAllUrls = (sourceEvidence: SourceTypeEvidence): UrlWithScreenshot[] => {
    const allUrls = new Map<string, UrlWithScreenshot>();
    for (const method of RESOLUTION_METHOD_ORDER) {
      const methodEvidence = sourceEvidence[method];
      if (methodEvidence?.urls) {
        for (const urlInfo of methodEvidence.urls.values()) {
          if (!allUrls.has(urlInfo.url)) {
            allUrls.set(urlInfo.url, urlInfo);
          }
        }
      }
    }
    return Array.from(allUrls.values())
      .sort((a, b) => {
        const priorityA = URL_TYPE_PRIORITY[a.urlType || ''] ?? 99;
        const priorityB = URL_TYPE_PRIORITY[b.urlType || ''] ?? 99;
        return priorityA - priorityB;
      });
  };

  // Render just URLs for web evidence (no method descriptions)
  const renderWebUrls = (sourceEvidence: SourceTypeEvidence) => {
    const urlEntries = getAllUrls(sourceEvidence);
    if (urlEntries.length === 0) return null;

    return (
      <>
        {urlEntries.slice(0, 5).map((urlInfo: UrlWithScreenshot) => (
          <div key={urlInfo.url} className="institution-evidence__method">
            <div className="institution-evidence__method-text">
              <BiSubdirectoryRight className="institution-evidence__method-icon" size={14} />
              {urlInfo.urlStatus === 'UNREACHABLE' && (
                <AiFillWarning
                  className="institution-evidence__url-warning"
                  size={14}
                  data-tooltip-id="main-tooltip"
                  data-tooltip-content="This URL was unreachable last time we checked. It may have been taken down."
                />
              )}
              {urlInfo.screenshotKey ? (
                <span
                  className={`institution-evidence__url-link${urlInfo.urlStatus === 'UNREACHABLE' ? ' institution-evidence__url-unreachable' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    const key = urlInfo.screenshotKey!.startsWith('url/')
                      ? urlInfo.screenshotKey!.slice(4)
                      : urlInfo.screenshotKey!;
                    onSelectScreenshot({
                      screenshot: key,
                      screenshot_size: null,
                      boxes: []
                    });
                  }}
                >
                  {General.ellipsisInMiddle(General.normalizeUrl(urlInfo.url) || '', 70)}
                </span>
              ) : (
                <span className={urlInfo.urlStatus === 'UNREACHABLE' ? 'institution-evidence__url-unreachable' : ''}>
                  {General.ellipsisInMiddle(General.normalizeUrl(urlInfo.url) || '', 70)}
                </span>
              )}
            </div>
          </div>
        ))}
      </>
    );
  };

  const renderMethods = (sourceEvidence: SourceTypeEvidence, instType: string | undefined) => {
    const methods = RESOLUTION_METHOD_ORDER.filter(m => sourceEvidence[m]);
    return methods.map((method) => {
      const methodEvidence = sourceEvidence[method];
      const urlEntries = Array.from(methodEvidence.urls.values())
        .sort((a: UrlWithScreenshot, b: UrlWithScreenshot) => {
          const priorityA = URL_TYPE_PRIORITY[a.urlType || ''] ?? 99;
          const priorityB = URL_TYPE_PRIORITY[b.urlType || ''] ?? 99;
          return priorityA - priorityB;
        });

      return (
        <div key={method} className="institution-evidence__method">
          <div className="institution-evidence__method-text">
            <BiSubdirectoryRight className="institution-evidence__method-icon" size={14} />
            {getEvidenceDescription(method, Array.from(methodEvidence.values), instType, firstName)}
          </div>
          {urlEntries.length > 0 && (method === 'URL_DOMAIN' || method === 'URL_PREFIX') && (
            <div className="institution-evidence__urls">
              {urlEntries.slice(0, 3).map((urlInfo: UrlWithScreenshot) => (
                <div key={urlInfo.url} className="institution-evidence__url">
                  <BiSubdirectoryRight className="institution-evidence__url-icon" size={14} />
                  {urlInfo.urlStatus === 'UNREACHABLE' && (
                    <AiFillWarning
                      className="institution-evidence__url-warning"
                      size={14}
                      data-tooltip-id="main-tooltip"
                      data-tooltip-content="This URL was unreachable last time we checked. It may have been taken down."
                    />
                  )}
                  {urlInfo.screenshotKey ? (
                    <span
                      className="institution-evidence__url-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        const key = urlInfo.screenshotKey!.startsWith('url/')
                          ? urlInfo.screenshotKey!.slice(4)
                          : urlInfo.screenshotKey!;
                        onSelectScreenshot({
                          screenshot: key,
                          screenshot_size: null,
                          boxes: []
                        });
                      }}
                    >
                      {General.ellipsisInMiddle(General.normalizeUrl(urlInfo.url) || '', 70)}
                    </span>
                  ) : (
                    <span>
                      {General.ellipsisInMiddle(General.normalizeUrl(urlInfo.url) || '', 70)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div className="institution-evidence">
      <div className="institution-evidence__header">Institutions ({belongsTo.length})</div>
      {sortedInstitutions.map((bt: any, index: number) => {
        const evidence = institutionEvidence.get(bt.institution?.id);
        const hasWebEvidence = evidence && Object.keys(evidence.web).length > 0;
        const hasDistrictEvidence = evidence && Object.keys(evidence.district).length > 0;
        const hasAnyEvidence = hasWebEvidence || hasDistrictEvidence;

        // For districts without direct evidence, check if relationship is inferred from school
        const isInferredFromSchool = bt.institution?.type === 'DISTRICT' &&
          !hasAnyEvidence && hasSchoolWithEvidence;

        const name = firstName || 'Contact';
        const typeLabel = getInstitutionTypeLabel(bt.institution?.type);

        return (
          <div key={index} className="institution-evidence__institution">
            <div className="institution-evidence__institution-header">
              <Link to={`/directory/institutions/${bt.institution?.id}`} className="institution-evidence__institution-name">
                {bt.institution?.name}
              </Link>
              {bt.institution?.type && (
                <UniversalTag category={bt.institution.type} />
              )}
            </div>
            {isInferredFromSchool && (
              <div className="institution-evidence__category">
                <div className="institution-evidence__category-item">
                  <div className="institution-evidence__category-text">
                    <BiSubdirectoryRight className="institution-evidence__icon" size={18} />
                    {name} was found at a school within this district
                  </div>
                </div>
              </div>
            )}
            {hasWebEvidence && (() => {
              const recency = formatRecency(evidence!.webMostRecentAt);
              // Get domain from EMAIL_DOMAIN or URL_DOMAIN evidence (these prove domain ownership)
              const emailDomainEvidence = evidence!.web['EMAIL_DOMAIN'];
              const urlDomainEvidence = evidence!.web['URL_DOMAIN'];
              const urlPrefixEvidence = evidence!.web['URL_PREFIX'];
              const ownedDomains = new Set<string>();
              if (emailDomainEvidence?.values) {
                emailDomainEvidence.values.forEach(v => ownedDomains.add(v));
              }
              if (urlDomainEvidence?.values) {
                urlDomainEvidence.values.forEach(v => ownedDomains.add(v));
              }
              const domainsJsx = formatDomainsJsx(Array.from(ownedDomains));
              // If only URL_PREFIX evidence (no domain ownership proof), say "on a page associated with"
              const isPrefixOnly = urlPrefixEvidence && !emailDomainEvidence && !urlDomainEvidence;
              let label;
              if (domainsJsx) {
                label = <>{name} was found on {typeLabel} website {domainsJsx}</>;
              } else if (isPrefixOnly) {
                label = <>{name} was found on a page associated with this {typeLabel}</>;
              } else {
                label = <>{name} was found on {typeLabel} website</>;
              }
              return (
                <CollapsibleEvidence emoji="🌍" label={label} recency={recency}>
                  {renderWebUrls(evidence!.web)}
                </CollapsibleEvidence>
              );
            })()}
            {hasDistrictEvidence && (() => {
              const recency = formatRecency(evidence!.districtMostRecentAt);
              return (
                <div className="institution-evidence__category">
                  <div className="institution-evidence__category-item">
                    <div className="institution-evidence__category-text">
                      <BiSubdirectoryRight className="institution-evidence__icon" size={18} />
                      🏷️ {name} was verified by the {typeLabel}{recency ? ` ${recency}` : ''}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        );
      })}
    </div>
  );
}
